//
// src/pages/PrinterTest.js
//
// Tenant-aware printer test page.
//
// This component:
// - Uses the active authenticated tenant/session through the shared API client
// - Prints an existing sale from the current tenant workspace
// - Avoids fabricated receipt IDs
// - Surfaces useful backend errors
//

import {
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import PrintIcon from "@mui/icons-material/Print";

import { printReceipt } from "../utils/printer";


// =====================================================
// Helpers
// =====================================================

function getStoredUser() {
  const rawUser =
    localStorage.getItem("user");

  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser);
  } catch {
    return null;
  }
}


function getActiveTenant() {
  const storedTenant =
    localStorage.getItem("tenant_id");

  if (
    storedTenant &&
    storedTenant.trim()
  ) {
    return storedTenant
      .trim()
      .toLowerCase();
  }

  const user =
    getStoredUser();

  if (
    user?.tenant_id
  ) {
    return String(
      user.tenant_id
    )
      .trim()
      .toLowerCase();
  }

  return null;
}


// =====================================================
// Component
// =====================================================

export default function PrinterTest() {
  const [
    saleId,
    setSaleId,
  ] = useState("");


  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  const [
    success,
    setSuccess,
  ] = useState("");


  const tenantId =
    useMemo(
      () => getActiveTenant(),
      []
    );


  // ===================================================
  // Validation
  // ===================================================

  const parsedSaleId =
    Number.parseInt(
      saleId,
      10
    );


  const validSaleId =
    Number.isInteger(
      parsedSaleId
    ) &&
    parsedSaleId > 0;


  // ===================================================
  // Print Existing Sale
  // ===================================================

  const handleTestPrint =
    async () => {
      if (loading) {
        return;
      }


      setError("");
      setSuccess("");


      if (!tenantId) {
        setError(
          "No active workspace could be identified. Please sign in again."
        );

        return;
      }


      if (!validSaleId) {
        setError(
          "Please enter a valid numeric sale ID."
        );

        return;
      }


      setLoading(true);


      try {
        await printReceipt({
          sale: {
            id: parsedSaleId,
          },
        });


        setSuccess(
          `Invoice #${parsedSaleId} was sent to the printer for workspace "${tenantId}".`
        );

      } catch (err) {
        console.error(
          "Printer test failed:",
          err
        );


        setError(
          err?.message ||
          "The receipt could not be printed."
        );

      } finally {
        setLoading(false);
      }
    };


  // ===================================================
  // Render
  // ===================================================

  return (
    <Container
      maxWidth="sm"
      sx={{
        py: {
          xs: 4,
          sm: 6,
        },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: {
            xs: 3,
            sm: 4,
          },

          borderRadius: 3,
        }}
      >
        <Stack
          spacing={3}
        >
          <Box
            sx={{
              textAlign: "center",
            }}
          >
            <PrintIcon
              color="primary"
              sx={{
                fontSize: 48,
                mb: 1,
              }}
            />

            <Typography
              variant="h4"
              component="h1"
              gutterBottom
            >
              Printer Test
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Print an existing receipt from the active
              business workspace.
            </Typography>
          </Box>


          {/* ============================================
              Workspace
          ============================================ */}

          <Alert
            severity={
              tenantId
                ? "info"
                : "warning"
            }
          >
            {tenantId
              ? (
                  <>
                    Active workspace:{" "}
                    <strong>
                      {tenantId}
                    </strong>
                  </>
                )
              : (
                  "No active workspace is available."
                )}
          </Alert>


          {/* ============================================
              Messages
          ============================================ */}

          {error && (
            <Alert
              severity="error"
            >
              {error}
            </Alert>
          )}


          {success && (
            <Alert
              severity="success"
            >
              {success}
            </Alert>
          )}


          {/* ============================================
              Sale ID
          ============================================ */}

          <TextField
            fullWidth
            required
            type="number"

            label="Existing Sale ID"

            placeholder="e.g. 7"

            value={
              saleId
            }

            onChange={
              (event) => {
                setSaleId(
                  event.target.value
                );

                if (error) {
                  setError("");
                }

                if (success) {
                  setSuccess("");
                }
              }
            }

            disabled={
              loading
            }

            inputProps={{
              min: 1,
              step: 1,
            }}

            helperText={
              "Enter the numeric invoice ID of a sale already recorded in this workspace."
            }
          />


          {/* ============================================
              Print
          ============================================ */}

          <Button
            variant="contained"
            size="large"
            fullWidth

            startIcon={
              loading
                ? null
                : <PrintIcon />
            }

            onClick={
              handleTestPrint
            }

            disabled={
              loading ||
              !tenantId ||
              !validSaleId
            }

            sx={{
              py: 1.4,
              fontWeight: 700,
            }}
          >
            {loading ? (
              <>
                <CircularProgress
                  size={22}
                  color="inherit"
                  sx={{
                    mr: 1.5,
                  }}
                />

                Sending Receipt...
              </>
            ) : (
              "Print Existing Receipt"
            )}
          </Button>


          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            The receipt is rebuilt by the backend from the
            active tenant's stored transaction data. The
            browser does not supply tenant branding or
            receipt contents.
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
}